import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useLocation } from 'react-router-dom';

import { Button, IconButton, Tooltip } from '@mui/material';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import ArrowBackIosNewOutlinedIcon from '@mui/icons-material/ArrowBackIosNewOutlined';
import InfoIcon from '@mui/icons-material/Info';

import { Routes } from 'constants/pages';
import { SelectedContainerContext } from 'pages/TrackAndTrace/contexts/SelectedContainerContextProvider';
import { useSnackbar } from 'common/hooks/snackbar';
import { useCustomSearchParams } from 'common/hooks/customSearchParams';
import { WATCH_LIST_QUERY_PARAMS } from 'pages/TrackAndTrace/constants';
import { useSegment } from 'common/hooks/segment';
import { Permission, useHasPermission } from 'utils/permissions';
import { Cell } from './Cell';
import { CellProps } from './types';
import { archiveContainers } from '../../controls/actions/utils';

export const ActionsCell: React.FC<CellProps> = (params) => {
  const { trackEventNoSlack } = useSegment();

  const [, setSelectedContainer] = React.useContext(SelectedContainerContext);
  const { searchParams, setSearchParams } = useCustomSearchParams(WATCH_LIST_QUERY_PARAMS);
  const queryClient = useQueryClient();
  const { search } = useLocation();
  const displaySnackbar = useSnackbar();
  const {
    row: { containerNumber, transaction },
  } = params;

  const handleArchiveContainer = async () => {
    trackEventNoSlack('Container Archived', {
      feature: 'trackAndTrace',
      source: 'row',
      nbContainers: 1,
      containers: containerNumber,
    });
    await archiveContainers(
      searchParams.containerNumbers,
      [containerNumber],
      [transaction],
      null,
      setSearchParams,
      queryClient
    );
    displaySnackbar({
      text: `Container ${containerNumber} archived.`,
    });
  };

  const accessPickupTrackChanges = useHasPermission([Permission.TrackNTracePickupTrackChanges]);

  return (
    <Cell className="flex w-full h-full items-center justify-between px-3">
      <Tooltip
        title={
          <div className="flex items-center space-x-1">
            <InfoIcon style={{ fontSize: 14 }} />
            <h5 className="font-medium">Click the icon to archive your container</h5>
          </div>
        }
        classes={{ tooltip: 'shadow-lg mr-0 h-6' }}
        placement="left"
      >
        <IconButton onClick={handleArchiveContainer}>
          <Inventory2OutlinedIcon className="text-gray-600" />
        </IconButton>
      </Tooltip>
      {accessPickupTrackChanges && (
        <Link
          to={`${Routes.ContainerTracking}/${containerNumber}${search}`}
          className="cursor-pointer text-blue-900 underline"
          onClick={() =>
            setSelectedContainer({
              watchListRow: params.row,
              return: window.location.search,
            })
          }
        >
          <Button
            variant="outlined"
            className="p-1.5"
            style={{
              minWidth: 0,
            }}
          >
            <ArrowBackIosNewOutlinedIcon style={{ fontSize: 9 }} />
          </Button>
        </Link>
      )}
    </Cell>
  );
};
ActionsCell.displayName = 'ContainerTable.ActionsCell';
